import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL, getAuthHeaders, handleUnauthorized } from "../../utils/api";
import {
  clearParentNotifications,
  filterVisibleParentNotifications,
  loadParentNotificationReads,
  markParentNotificationsRead,
} from "../../utils/parentNotifications";
import "./parent.css";

function typeLabel(type) {
  if (type === "homework") return "Homework";
  if (type === "remark") return "Remark";
  if (type === "challan") return "Challan";
  return "Update";
}

function ParentNotifications({ onLogout }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reads, setReads] = useState(() => loadParentNotificationReads());

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/parents/me/notifications`, {
          headers: getAuthHeaders(),
        });
        if (res.status === 401) {
          handleUnauthorized(onLogout);
          return;
        }
        if (!res.ok) throw new Error("Failed to load notifications");
        const data = await res.json();
        const list = filterVisibleParentNotifications(
          Array.isArray(data.items) ? data.items : [],
        );
        if (!alive) return;
        setItems(list);
        // Visiting the notifications page counts as viewed — clear sidebar badge.
        if (list.length) {
          markParentNotificationsRead(list.map((item) => item.id));
          setReads(loadParentNotificationReads());
        }
      } catch (err) {
        console.error(err);
        if (alive) setItems([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [onLogout]);

  const markRead = (id) => {
    markParentNotificationsRead([id]);
    setReads(loadParentNotificationReads());
  };

  const clearAll = () => {
    if (!items.length) return;
    clearParentNotifications(items.map((item) => item.id));
    setItems([]);
    setReads(loadParentNotificationReads());
  };

  if (loading) {
    return (
      <div className="pp-loading">
        <div className="pp-spinner" />
        <p>Loading notifications…</p>
      </div>
    );
  }

  return (
    <div className="pp-page pp-animate-in">
      <header className="pp-page-header pp-notif-header">
        {items.length > 0 ? (
          <button type="button" className="pp-btn pp-btn--ghost" onClick={clearAll}>
            Clear all
          </button>
        ) : null}
      </header>

      {items.length === 0 ? (
        <div className="pp-empty">No notifications yet.</div>
      ) : (
        <ul className="pp-notif-list">
          {items.map((item) => {
            const unread = !reads.includes(item.id);
            return (
              <li key={item.id} className={`pp-notif-item ${unread ? "is-unread" : ""}`}>
                <button
                  type="button"
                  className="pp-notif-btn"
                  onClick={() => {
                    markRead(item.id);
                    if (item.link) navigate(item.link);
                  }}
                >
                  <span className={`pp-notif-type pp-notif-type--${item.type}`}>
                    {typeLabel(item.type)}
                  </span>
                  <div className="pp-notif-body">
                    <strong>{item.title}</strong>
                    <span>
                      {item.student_name}
                      {item.course_name ? ` · ${item.course_name}` : ""}
                    </span>
                    <p>{item.message}</p>
                    <small className="pp-muted">{item.created_at || ""}</small>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default ParentNotifications;
